<script setup lang="ts">
/**
 * Static-text replacement for any interactive control in print mode.
 *
 * Renders `{{ label }}: {{ value }}` as a plain inline-block so a PDF
 * exporting an insight with filters reads as "Region: Metro" rather
 * than showing a disabled-looking dropdown that prints as broken UI.
 *
 * Used by InsightSelect / Multiselect / Toggle / Date / DateRange /
 * Number / NumberRange / CheckboxGroup / Search via a `v-if="isPrint()"`
 * gate. The `label` prop is the control's user-visible label (whatever
 * the author supplied in `opts.label`); `value` is the formatted
 * current value the live control would have shown above its widget.
 */
defineProps<{
  /** Label text — usually `opts.label` from the live control. */
  label?: string;
  /**
   * Formatted current value. May be empty (e.g. unset date picker);
   * we still render the row so the reader sees the filter was
   * intentionally left blank, not omitted from the report.
   */
  value: string;
}>();
</script>

<template>
  <span class="print-static">
    <span v-if="label" class="print-static-label">{{ label }}:</span>
    <span class="print-static-value">{{ value || "—" }}</span>
  </span>
</template>

<style scoped>
.print-static {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
  color: var(--color-text, #1a1f2e);
  padding: 4px 0;
}
.print-static-label {
  color: var(--color-text-muted, #6b7484);
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.print-static-value {
  color: var(--color-text, #1a1f2e);
}
</style>
